import { format } from 'date-fns';

import { DATE_FORMATS, type DateInput } from './formats';
import { dateLocale } from './locales';
import { parseDate, toDate } from './parse';
import { wallClockDate } from './wallClock';

function patternFor(patterns: Record<string, string>, locale: string): string {
    return patterns[locale] ?? patterns.en;
}

function formatIn(date: Date, pattern: string, locale: string): string {
    return format(date, pattern, { locale: dateLocale(locale) });
}

/**
 * A `yyyy-MM-dd` day in a date-fns pattern with the locale's month and day names, for
 * chart labels. `fallback` is returned when the day does not parse.
 *
 * @example
 * formatDateByPattern('2024-10-16', 'MMM. d', 'en'); // "Oct. 16"
 * formatDateByPattern('2024-10-16', 'd. MMM', 'nb'); // "16. okt."
 * formatDateByPattern('2024-10-16', 'd MMM', 'es'); // "16 oct."
 * formatDateByPattern('invalid', 'MMM. d', 'en', '-'); // "-"
 */
export function formatDateByPattern(
    dateStr: string,
    formatPattern: string,
    locale: string,
    fallback = ''
): string {
    const date = parseDate(dateStr, DATE_FORMATS.internal);
    if (!date) return fallback;
    return formatIn(date, formatPattern, locale);
}

const SHORT_DATE: Record<string, string> = {
    en: 'MMM d, yyyy', // Oct 5, 2025
    nb: 'd.MMM yyyy', // 5.okt. 2025
    es: 'd MMM yyyy', // 5 oct. 2025
};

/** A short local date with the abbreviated month, e.g. "Oct 5, 2025" or "5.okt. 2025". */
export function formatDateShortLocalized(date: DateInput, locale: string): string {
    const d = toDate(date);
    if (!d) return '';
    return formatIn(d, patternFor(SHORT_DATE, locale), locale);
}

const DATE_TIME_HOUR: Record<string, string> = {
    en: 'MMM d, HH:mm',
    nb: 'd. MMM HH:mm',
    es: 'd MMM HH:mm',
};

/**
 * An hourly bucket's key, a site wall clock (`yyyy-MM-dd HH:mm:ss`, see `wallClock.ts`), as
 * month, day, hour and minute for chart labels, exactly as the site's clock read it. Read in
 * the browser's zone, the hour that zone skips at its spring switch would take the next hour's
 * label, so two buckets would share one and the skipped one would vanish.
 *
 * @example
 * formatWallClockHourLocalized('2025-12-11 14:00:00', 'en'); // "Dec 11, 14:00"
 * formatWallClockHourLocalized('2025-12-11 14:00:00', 'nb'); // "11. des. 14:00"
 * formatWallClockHourLocalized('2026-03-29 02:00:00', 'en'); // "Mar 29, 02:00", in Oslo too
 */
export function formatWallClockHourLocalized(
    wallClock: string | null | undefined,
    locale: string
): string {
    const d = wallClockDate(wallClock);
    if (!d) return '';
    return formatIn(d, patternFor(DATE_TIME_HOUR, locale), locale);
}
